import { PieChart } from '@mantine/charts';
import { getThemeColor, useMantineTheme } from '@mantine/core';
import { IconChartBar } from '@tabler/icons-react';
import fileDownload from 'js-file-download';
import React, { useCallback, useId, useMemo, useState } from 'react';
import { ChatChartSpecDto } from 'src/api/FiretrolApiClient';
import { ChatCartesianChart } from 'src/app/chat/ChatCartesianChart';
import {
  buildChartCsv,
  chatCsvFileName,
  CSV_MIME_TYPE,
} from 'src/app/chat/chatCsv';
import {
  CHAT_CHART_COLORS,
  CHAT_PIE_SIZE,
  DisplayableChatChartSpec,
  chartUnit,
  chartValueAt,
  drawablePieSlices,
  formatChartValue,
  isDisplayableChart,
} from 'src/app/chat/chatChartFormat';
import {
  CHAT_TABLE_COLUMN_MIN_WIDTH,
  CHAT_TABLE_NUMERIC_CELL_STYLE,
  CHAT_TABLE_SCROLL_AREA_PROPS,
} from 'src/app/chat/chatTableFormat';
import { Alert } from 'src/design/atoms/Alert';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Box } from 'src/design/atoms/Box';
import { ColorSwatch } from 'src/design/atoms/ColorSwatch';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Table } from 'src/design/molecules/Table';

export interface IChatChartProps {
  spec: ChatChartSpecDto;
}

const PieChartWithLegend = React.memo<{ spec: DisplayableChatChartSpec }>(
  ({ spec }) => {
    const theme = useMantineTheme();
    const unit = chartUnit(spec);
    const slices = useMemo(
      () =>
        spec.points.map((point, index) => ({
          name: point.x,
          value: chartValueAt(point, 0),
          color: CHAT_CHART_COLORS[index],
        })),
      [spec.points],
    );
    const drawable = useMemo(() => drawablePieSlices(slices), [slices]);

    return (
      <Stack gap="xs" align="center">
        <PieChart
          data={drawable}
          size={CHAT_PIE_SIZE}
          withTooltip
          tooltipDataSource="segment"
          valueFormatter={(value) => formatChartValue(value, unit)}
        />
        <Stack gap={4} w="100%" data-testid="chat-chart-pie-legend">
          {slices.map((slice, index) => (
            <Group key={index} gap="xs" wrap="nowrap" justify="space-between">
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <ColorSwatch
                  color={getThemeColor(slice.color, theme)}
                  size={10}
                  withShadow={false}
                  style={{ flexShrink: 0 }}
                />
                <Text size="xs" truncate="end">
                  {slice.name}
                </Text>
              </Group>
              <Text size="xs" style={CHAT_TABLE_NUMERIC_CELL_STYLE}>
                {formatChartValue(slice.value, unit)}
              </Text>
            </Group>
          ))}
        </Stack>
      </Stack>
    );
  },
);

const ChatChartTable: React.FC<{ spec: ChatChartSpecDto; id: string }> = ({
  spec,
  id,
}) => (
  <Box id={id}>
    <Table.ScrollContainer
      minWidth={(spec.series.length + 1) * CHAT_TABLE_COLUMN_MIN_WIDTH}
      scrollAreaProps={CHAT_TABLE_SCROLL_AREA_PROPS}
    >
      <Table withTableBorder fz="xs" verticalSpacing={4}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{spec.xLabel}</Table.Th>
            {spec.series.map((s, index) => (
              <Table.Th key={index} ta="right">
                {s.label}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {spec.points.map((point, rowIndex) => (
            <Table.Tr key={rowIndex}>
              <Table.Td>{point.x}</Table.Td>
              {spec.series.map((s, index) => (
                <Table.Td
                  key={index}
                  ta="right"
                  style={CHAT_TABLE_NUMERIC_CELL_STYLE}
                >
                  {formatChartValue(chartValueAt(point, index), s.unit)}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  </Box>
);

/** @registry Renders a validated AI-assistant chart spec (bar, stacked bar, line, area, pie) with a Show data table toggle and a CSV download of that table; falls back to a muted notice for specs it cannot draw. */
export const ChatChart = React.memo<IChatChartProps>(({ spec }) => {
  const [showData, setShowData] = useState(false);
  const tableId = useId();
  const hasData =
    (spec.series?.length ?? 0) > 0 && (spec.points?.length ?? 0) > 0;
  const downloadCsv = useCallback(
    () =>
      fileDownload(
        buildChartCsv(spec),
        chatCsvFileName('chart', new Date()),
        CSV_MIME_TYPE,
      ),
    [spec],
  );

  return (
    <Stack gap="xs" w="100%" style={{ minWidth: 0 }} data-testid="chat-chart">
      {spec.title && (
        <Text fw={600} size="sm" c="bright">
          {spec.title}
        </Text>
      )}
      {!isDisplayableChart(spec) ? (
        <Alert
          variant="light"
          color="gray"
          icon={<IconChartBar size={16} />}
          p="xs"
        >
          <Text size="xs" c="dimmed">
            This chart cannot be displayed.
          </Text>
        </Alert>
      ) : spec.type === 'pie' ? (
        <PieChartWithLegend spec={spec} />
      ) : (
        <ChatCartesianChart spec={spec} />
      )}
      {hasData && (
        <>
          <Group gap="xs">
            <BasicButton
              size="compact-xs"
              variant="subtle"
              color="gray"
              aria-expanded={showData}
              aria-controls={showData ? tableId : undefined}
              onClick={() => setShowData((shown) => !shown)}
            >
              {showData ? 'Hide data' : 'Show data'}
            </BasicButton>
            <BasicButton
              size="compact-xs"
              variant="subtle"
              color="gray"
              aria-label={`Download CSV of ${spec.title || 'chart'} data`}
              onClick={downloadCsv}
            >
              Download CSV
            </BasicButton>
          </Group>
          {showData && <ChatChartTable spec={spec} id={tableId} />}
        </>
      )}
    </Stack>
  );
});
